import { useEffect, useState } from 'react';
import { ShieldCheck, RefreshCw, AlertTriangle, Building2, Users } from 'lucide-react';
import { systemApi } from '../../../api/client';

export function SystemSecurityPage() {
  const [orgCount, setOrgCount]       = useState<number | null>(null);
  const [isLoading, setIsLoading]     = useState(true);
  const [error, setError]             = useState<string | null>(null);

  useEffect(() => {
    systemApi.listOrganizations()
      .then((orgs) => setOrgCount(orgs.length))
      .catch((err) => setError((err as Error).message))
      .finally(() => setIsLoading(false));
  }, []);

  return (
    <div className="max-w-4xl mx-auto px-6 py-8 space-y-6">
      <div>
        <h2 className="text-lg font-semibold text-gray-100">System Security</h2>
        <p className="text-sm text-gray-500 mt-1">
          Platform-wide security posture and controls.
        </p>
      </div>

      {error && (
        <div className="flex items-center gap-3 p-3 bg-loss/10 border border-loss/20 rounded-lg">
          <AlertTriangle className="w-4 h-4 text-loss flex-shrink-0" />
          <p className="text-sm text-loss">{error}</p>
        </div>
      )}

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500 py-8">
          <RefreshCw className="w-4 h-4 animate-spin" />
          Loading…
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="op-panel p-5 flex items-center gap-4">
            <div className="p-2.5 rounded-lg bg-brand/10 border border-brand/20">
              <Building2 className="w-5 h-5 text-brand" />
            </div>
            <div>
              <p className="text-2xl font-bold text-gray-100">{orgCount ?? '—'}</p>
              <p className="text-xs text-gray-500 mt-0.5">Total Organizations</p>
            </div>
          </div>

          <div className="op-panel p-5 flex items-center gap-4">
            <div className="p-2.5 rounded-lg bg-win/10 border border-win/20">
              <ShieldCheck className="w-5 h-5 text-win" />
            </div>
            <div>
              <p className="text-sm font-semibold text-win">Active</p>
              <p className="text-xs text-gray-500 mt-0.5">RLS Policies Enforced</p>
            </div>
          </div>

          <div className="op-panel p-5 flex items-center gap-4">
            <div className="p-2.5 rounded-lg bg-win/10 border border-win/20">
              <Users className="w-5 h-5 text-win" />
            </div>
            <div>
              <p className="text-sm font-semibold text-win">Active</p>
              <p className="text-xs text-gray-500 mt-0.5">System Admin Access Control</p>
            </div>
          </div>
        </div>
      )}

      <div className="op-panel p-4 bg-surface-2/50">
        <p className="text-xs text-gray-500">
          <span className="font-medium text-gray-400">System admin designation: </span>
          Managed directly via the <code className="text-brand-light">system_admins</code> database table.
          No app-level UI for promotion — requires direct database access.
        </p>
      </div>
    </div>
  );
}
